export interface SharedChatMeta {
  public_id: string;
  title: string;
  created_at: string;
}

export interface SharedChatCreateResponse {
  id: string;
  public_id: string;
  public_url: string;
  session_id?: string | null;
  conversation_id?: string;
  agent_group_id?: string;
  title: string;
  created_at: string;
}

export interface SharedChatInfo {
  id: string;
  public_id: string;
  public_url: string;
  session_id?: string | null;
  conversation_id?: string;
  agent_group_id?: string;
  title: string;
  status: string;
  created_at: string;
}

export type WebAgentRawSSEEvent = {
  eventType: string;
  data: string;
  id?: string;
};

export async function fetchSharedChatMeta(
  publicId: string
): Promise<SharedChatMeta> {
  const response = await fetch(
    `/v1/public/shared-chats/${encodeURIComponent(publicId)}`
  );
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? 'Shared chat not found'
        : 'Failed to load shared chat'
    );
  }
  return response.json();
}

export async function openSharedChatStream(params: {
  publicId: string;
  signal: AbortSignal;
  lastEventId?: string;
  onEvent: (event: WebAgentRawSSEEvent) => void;
  onOpen?: () => void;
}): Promise<void> {
  const headers: HeadersInit = {
    accept: 'text/event-stream',
  };
  const lastEventId = normalizeSharedChatEventId(params.lastEventId);
  if (lastEventId) {
    headers['Last-Event-ID'] = lastEventId;
  }

  const response = await fetch(
    `/v1/public/shared-chats/${encodeURIComponent(params.publicId)}/stream`,
    {
      method: 'GET',
      headers,
      cache: 'no-store',
      signal: params.signal,
    }
  );

  if (!response.ok || !response.body) {
    throw new Error(
      response.status === 404
        ? 'Shared chat not found'
        : 'Failed to connect to shared chat stream'
    );
  }

  params.onOpen?.();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = 'message';
  let currentDataLines: string[] = [];
  let currentEventId: string | undefined;

  const flush = () => {
    if (currentDataLines.length === 0) {
      currentEventType = 'message';
      currentEventId = undefined;
      return;
    }

    params.onEvent({
      eventType: currentEventType,
      data: currentDataLines.join('\n'),
      id: currentEventId,
    });

    currentDataLines = [];
    currentEventType = 'message';
    currentEventId = undefined;
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line === '') {
          flush();
        } else if (line.startsWith('event:')) {
          currentEventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          currentDataLines.push(line.slice(5));
        } else if (line.startsWith('id:')) {
          currentEventId = line.slice(3).trim();
        }
        // Ignore comments and unknown lines
      }
    }
  } finally {
    reader.releaseLock();
  }

  flush();
}

export async function streamSharedChat(
  publicId: string,
  signal: AbortSignal,
  onEvent: (event: WebAgentRawSSEEvent) => void,
  options?: {
    onOpen?: () => void;
    onRetry?: (info: {
      attempt: number;
      retryInMs: number;
      error: Error;
    }) => void;
  }
): Promise<void> {
  let lastEventId: string | undefined;

  const connect = async (attempt: number): Promise<void> => {
    if (signal.aborted) {
      return;
    }

    try {
      await openSharedChatStream({
        publicId,
        signal,
        lastEventId,
        onEvent: event => {
          if (event.id !== undefined) {
            lastEventId = normalizeSharedChatEventId(event.id);
          }
          onEvent(event);
        },
        onOpen: () => options?.onOpen?.(),
      });

      if (signal.aborted) {
        return;
      }

      // Stream ended normally — reconnect after a short delay
      const retryInMs = 1000;
      options?.onRetry?.({
        attempt: 1,
        retryInMs,
        error: new Error('Shared chat stream disconnected'),
      });
      await new Promise(resolve => setTimeout(resolve, retryInMs));
      await connect(1);
    } catch (error) {
      if (signal.aborted) {
        return;
      }

      // Don't retry 404 or other non-retryable errors
      if (
        error instanceof Error &&
        (error.message.includes('not found') || error.message.includes('404'))
      ) {
        throw error;
      }

      const retryInMs = Math.min(1000 * Math.pow(2, attempt - 1), 30000);
      options?.onRetry?.({
        attempt,
        retryInMs,
        error: error instanceof Error ? error : new Error('Stream error'),
      });
      await new Promise(resolve => setTimeout(resolve, retryInMs));
      await connect(attempt + 1);
    }
  };

  await connect(1);
}

function normalizeSharedChatEventId(
  value: string | undefined
): string | undefined {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : undefined;
}

// --- Authenticated API for creating/managing shared chats ---

export type ShareChatParams = {
  conversationId: string;
  agentGroupId: string;
  title: string;
};

export async function shareChat(
  params: ShareChatParams
): Promise<SharedChatCreateResponse> {
  const response = await fetch('/v1/shared-chats', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      conversation_id: params.conversationId,
      agent_group_id: params.agentGroupId,
      title: params.title,
    }),
  });
  if (!response.ok) {
    throw new Error('Failed to share chat');
  }
  return response.json();
}

export async function getShareByConversation(
  conversationId: string
): Promise<SharedChatInfo | null> {
  const response = await fetch(
    `/v1/shared-chats/by-conversation/${encodeURIComponent(conversationId)}?empty_status=204`,
    { credentials: 'include' }
  );
  if (response.status === 204 || response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error('Failed to check share status');
  }
  return response.json();
}

export async function revokeSharedChat(id: string): Promise<void> {
  const response = await fetch(`/v1/shared-chats/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to revoke shared chat');
  }
}
